import React, { useState, useEffect } from 'react';
import { withFirebase } from '../../Firebase';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Table,
  Input,
  FormGroup,
  Label,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const MappingManager = ({ firebase }) => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentMapping, setCurrentMapping] = useState({
    naturalTerms: [],
    filterType: 'genre',
    value: ''
  });

  useEffect(() => {
    loadMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebase]);

  const loadMappings = async () => {
    setLoading(true);
    try {
      const mappingDocRef = doc(firebase.db, 'chatbotMappings', 'default');
      const docSnap = await getDoc(mappingDocRef);
      if (docSnap.exists()) {
        setMappings(docSnap.data().mappings || []);
      } else {
        setMappings([]);
      }
    } catch (err) {
      setError('Error loading mappings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMappings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const mappingDocRef = doc(firebase.db, 'chatbotMappings', 'default');
      await setDoc(mappingDocRef, {
        mappings,
        lastUpdated: firebase.db.FieldValue.serverTimestamp()
      });
      setSuccess('Mappings saved successfully');
    } catch (err) {
      setError('Error saving mappings: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (!modalOpen) {
      setCurrentMapping({
        naturalTerms: [],
        filterType: 'genre',
        value: ''
      });
    }
  };

  const handleAddMapping = () => {
    if (!currentMapping.naturalTerms.length || !currentMapping.value) {
      setError('Please fill in all fields');
      return;
    }

    // Format the value based on filterType
    let formattedValue = currentMapping.value;
    if (currentMapping.filterType === 'bpm') {
      const [min, max] = currentMapping.value.split('-');
      formattedValue = { min: parseInt(min), max: parseInt(max) };
    } else if (currentMapping.filterType === 'tags') {
      formattedValue = currentMapping.value.split(',').map(tag => tag.trim());
    }

    const newMapping = {
      ...currentMapping,
      value: formattedValue
    };

    setMappings([...mappings, newMapping]);
    toggleModal();
    setSuccess('Mapping added successfully. Don\'t forget to save changes!');
  };

  const handleDeleteMapping = (index) => {
    const updatedMappings = [...mappings];
    updatedMappings.splice(index, 1);
    setMappings(updatedMappings);
    setSuccess('Mapping removed. Don\'t forget to save changes!');
  };

  const handleTermsChange = (e) => {
    const terms = e.target.value
      .split(',')
      .map(term => term.trim())
      .filter(term => term.length > 0);
    
    setCurrentMapping({
      ...currentMapping,
      naturalTerms: terms
    });
  };

  if (loading) {
    return (
      <Card>
        <CardBody className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2">Loading mappings...</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="no-bg-card">
      <CardHeader>
        <h2 className="mb-0">Chatbot Term Mappings</h2>
      </CardHeader>
      <CardBody>
        {error && (
          <Alert color="danger" className="mb-4">
            {error}
          </Alert>
        )}
        {success && (
          <Alert color="success" className="mb-4">
            {success}
          </Alert>
        )}

        <p>
          Define how natural language terms map to specific filters in your system.
          These mappings help the chatbot understand user queries better.
        </p>

        <div className="d-flex justify-content-between mb-3">
          <Button color="primary" onClick={toggleModal}>
            Add New Mapping
          </Button>
          <Button 
            color="success" 
            onClick={handleSaveMappings} 
            disabled={saving}
          >
            {saving ? (
              <span>
                <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                Saving...
              </span>
            ) : 'Save All Mappings'}
          </Button>
        </div>

        {mappings.length === 0 ? (
          <Alert color="info">
            No mappings defined yet. Click "Add New Mapping" to create one.
          </Alert>
        ) : (
          <Table responsive striped>
            <thead>
              <tr>
                <th>Natural Terms</th>
                <th>Filter Type</th>
                <th>Value</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {mappings.map((mapping, index) => (
                <tr key={index}>
                  <td>{mapping.naturalTerms.join(', ')}</td>
                  <td>{mapping.filterType}</td>
                  <td>
                    {typeof mapping.value === 'object' 
                      ? JSON.stringify(mapping.value) 
                      : Array.isArray(mapping.value)
                        ? mapping.value.join(', ')
                        : mapping.value}
                  </td>
                  <td>
                    <Button 
                      color="danger" 
                      size="sm" 
                      onClick={() => handleDeleteMapping(index)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {/* Add/Edit Mapping Modal */}
        <Modal isOpen={modalOpen} toggle={toggleModal}>
          <ModalHeader toggle={toggleModal}>
            Add Natural Language Mapping
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="naturalTerms">Natural Language Terms (comma-separated)</Label>
              <Input 
                type="text" 
                id="naturalTerms" 
                placeholder="e.g., upbeat, energetic, fast" 
                onChange={handleTermsChange}
                value={currentMapping.naturalTerms.join(', ')}
              />
            </FormGroup>
            <FormGroup>
              <Label for="filterType">Filter Type</Label>
              <Input 
                type="select" 
                id="filterType"
                value={currentMapping.filterType}
                onChange={e => setCurrentMapping({...currentMapping, filterType: e.target.value})}
              >
                <option value="genre">Genre</option>
                <option value="bpm">BPM (Tempo)</option>
                <option value="key">Key</option>
                <option value="tags">Tags</option>
                <option value="mood">Mood</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="filterValue">Filter Value</Label>
              {currentMapping.filterType === 'bpm' ? (
                <Input 
                  type="text" 
                  id="filterValue" 
                  placeholder="e.g., 120-140 (min-max)" 
                  value={currentMapping.value}
                  onChange={e => setCurrentMapping({...currentMapping, value: e.target.value})}
                />
              ) : currentMapping.filterType === 'tags' ? (
                <Input 
                  type="text" 
                  id="filterValue" 
                  placeholder="e.g., uplifting, positive, summer (comma-separated)" 
                  value={currentMapping.value}
                  onChange={e => setCurrentMapping({...currentMapping, value: e.target.value})}
                />
              ) : (
                <Input 
                  type="text" 
                  id="filterValue" 
                  placeholder={`Enter ${currentMapping.filterType} value`}
                  value={currentMapping.value}
                  onChange={e => setCurrentMapping({...currentMapping, value: e.target.value})}
                />
              )}
              <small className="form-text text-muted">
                {currentMapping.filterType === 'bpm' 
                  ? 'Enter as min-max range, e.g., "120-140"'
                  : currentMapping.filterType === 'tags'
                    ? 'Enter comma-separated tags'
                    : `Enter a valid ${currentMapping.filterType} value that exists in your system`}
              </small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={handleAddMapping}>Add Mapping</Button>
            <Button color="secondary" onClick={toggleModal}>Cancel</Button>
          </ModalFooter>
        </Modal>
      </CardBody>
    </Card>
  );
};

export default withFirebase(MappingManager);